import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HealthProfessionalUser } from '../../../app/authentication';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../constants/endpoints';
import BaseController from '../../BaseController';
import { Inject } from '@odinjs/odin';
import { ActionAPI, DynamicFieldsAPI, FieldAPI, FieldsetAPI, FormAPI, Metadata, ModalProps, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import { FragmentNames, IntakesAndOutputsViewerAliases } from '../../../codegen';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { ApplicationRoute } from '../../../constants/routes';
import { DateFormatterUtil } from '@philips-emr/health-professional-components/src/utils';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { IntakesAndPutsContext } from '../intakes-and-outputs-service';

interface IntakesOutputsValuesProps {
  [key: string]: string;
}

interface AttributesProps {
  component: Metadata;
  values: IntakesOutputsValuesProps;
}

enum CreateType {
  INTAKE = 'G',
  OUTPUT = 'P',
}

@Controller({ fragment: FragmentNames.INTAKES_AND_OUTPUTS_VIEWER })
class IntakesAndOutputsViewerController extends BaseController {

  @Inject(IntakesAndOutputsViewerAliases.FORM_INTAKES_OUTPUTS_VIEWER)
  private formApi!: FormAPI;

  @Inject(IntakesAndOutputsViewerAliases.FIELDSET_DYNAMIC_FIELDS)
  private fieldSetVisualDynamicFieldApi!: FieldsetAPI;

  @Inject(IntakesAndOutputsViewerAliases.DYNAMIC_FIELDS)
  private dynamicFieldsVisualApi!: DynamicFieldsAPI;

  @Inject(IntakesAndOutputsViewerAliases.FIELD_DEVICE)
  private deviceFieldApi!: FieldAPI;

  @Inject(IntakesAndOutputsViewerAliases.FIELD_SIDE)
  private sideFieldApi!: FieldAPI;

  @Inject(IntakesAndOutputsViewerAliases.FIELD_TOPOGRAPHY)
  private topographyFieldApi!: FieldAPI;

  @Inject(IntakesAndOutputsViewerAliases.FIELD_NOTES_DESCRIPTION)
  private observationFieldApi!: FieldAPI;

  @Inject(IntakesAndOutputsViewerAliases.PANEL_ACTIONS)
  private panelActionsApi!: PanelAPI;

  @Inject(IntakesAndOutputsViewerAliases.ACTION_INACTIVATE_INTAKES_OUTPUTS)
  private actionInactivateApi!: ActionAPI;

  @Inject(IntakesAndOutputsViewerAliases.ACTION_NEW_INTAKES_OUTPUTS)
  private actionNewApi!: ActionAPI;

  handleVisibleFields(record: Record<string, Value>): void {
    if(record.device) {
      this.deviceFieldApi.setVisible(true);
    }

    if(record.side) {
      this.sideFieldApi.setVisible(true);
    }

    if(record.topography) {
      this.topographyFieldApi.setVisible(true);
    }

    if(record.observation) {
      this.observationFieldApi.setVisible(true);
    }
  }

  handleDynamicFields(): void {
    const { patientId, encounterId, sequenceNumber } = this.historyService.getCurrentRouteParams();
    this.fieldSetVisualDynamicFieldApi.setVisible(false);
    HttpService.get(Endpoint.INTAKES_OUTPUTS_ADDITIONAL_INFORMATION, { encounterId, patientId, sequenceNumber }).then((response) => {
      const responseBody = response.body as AttributesProps;
      const dynamicFields = responseBody.component as Metadata;
      this.context.set({ dynamicFields })

      if(responseBody.values) {
        this.fieldSetVisualDynamicFieldApi.setVisible(true);
        const keys = Object.keys(responseBody.values);
        const values = Object.values(responseBody.values);
        const dynamicValues = {} as IntakesOutputsValuesProps;

        keys.forEach((key, index) => {
          dynamicValues[key] = values[index];
        })
        const record = this.formApi.getRecord();
        this.formApi.setRecord({ ...record, ...dynamicValues });
      }
    });
  }

  @OnMount({target: IntakesAndOutputsViewerAliases.INTAKES_AND_OUTPUTS_VIEWER_LAYOUT})
  onMount(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({encounterId, patientId});
    this.panelActionsApi.setVisible(false);
  }

  @OnMount({ target: IntakesAndOutputsViewerAliases.FORM_INTAKES_OUTPUTS_VIEWER })
  onFormMount(): void {
    const { encounterId, patientId, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo && IntakesAndPutsContext.getAllowEdit();

    HttpService.get(Endpoint.INTAKES_OUTPUTS_DETAIL, {
      encounterId,
      patientId,
      sequenceNumber
    }
    ).then(response => {
      const user = foundationContext.getUser() as HealthProfessionalUser;
      const record = response.body as Record<string, Value>;
      const showInactivate = record.professionalCode === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

      this.actionInactivateApi.setEnabled(showInactivate);
      this.handleVisibleFields(record);
      const recordDate = DateFormatterUtil.timeStamp(String(record.recordDate))
      this.formApi.setData({...record, recordDate});
      this.handleDynamicFields();
    }).finally(() => {
      this.actionNewApi.setEnabled(allowInfo);
    });
  }

  @OnMount(IntakesAndOutputsViewerAliases.DYNAMIC_FIELDS)
  handleMountDynamicFields(): void {
    const dynamicFields = this.context.get('dynamicFields');
    if (dynamicFields) {
      this.dynamicFieldsVisualApi.setMetadata(dynamicFields);
    }
  }

  @OnClick({ target: IntakesAndOutputsViewerAliases.ACTION_NEW_INTAKES_OUTPUTS })
  onNew(): void {
    const allowInfo = InformationBarUtils.allowInfo && IntakesAndPutsContext.getAllowEdit();
    this.panelActionsApi.setVisible(allowInfo);
  }

  @OnClick({ target: IntakesAndOutputsViewerAliases.INTAKES_OPTION })
  onIntakeOptionClick(): void {
    applicationContext.redirect(ApplicationRoute.INTAKES_OUTPUTS_CREATE, { type: CreateType.INTAKE });
  }

  @OnClick({ target: IntakesAndOutputsViewerAliases.OUTPUTS_OPTION })
  onOutputOptionClick(): void {
    applicationContext.redirect(ApplicationRoute.INTAKES_OUTPUTS_CREATE, { type: CreateType.OUTPUT });
  }

  @OnClick({ target: IntakesAndOutputsViewerAliases.ACTION_INACTIVATE_INTAKES_OUTPUTS })
  onInactivate(): void {
    const evolutionId = this.context.get('evolutionId');
    const encounterId = this.context.get('encounterId');
    const patientId = this.context.get('patientId');

    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'intakes-and-outputs-inactivate',
      id: 'intakes-and-outputs-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: '',
    };

    modalProperties.contextData = {
      encounterId,
      evolutionId,
      patientId,
    }

    ModalService.openDefault(modalProperties);
  }
}

export default IntakesAndOutputsViewerController;
